import { Tag } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  bulkAddProspectsToProject,
  BulkAddProspectsToProjectPayload,
  bulkAddProspectsToSequence,
  BulkAddProspectsToSequencePayload,
  bulkCreateProspects,
  BulkCreateProspectsPayload,
  bulkDestroyProspects,
  BulkPromoteProspectsPayload,
  bulkPromoteToClient,
  bulkRenameProspects,
  BulkRenameProspectsPayload,
  bulkUpdateProspects,
  BulkUpdateProspectsPayload,
} from "./api"

export function useBulkUpdateProspects() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkUpdateProspectsPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkUpdateProspects(context, params)
    },
    onMutate: async (params: BulkUpdateProspectsPayload) => {
      await queryClient.cancelQueries({ queryKey: ["prospects"] })

      // Store previous data for all matching queries
      const previousQueries = queryClient.getQueriesData({ queryKey: ["prospects", "infinite"] })

      // Get the tag from cache if we're adding a tag
      let tagToAdd: Tag | undefined
      if (params.tag_id) {
        const tagsData = queryClient.getQueryData<{ tags: Tag[] }>(["tags", "household"])
        tagToAdd = tagsData?.tags?.find((tag) => tag.id === params.tag_id)
      }

      // Update all matching infinite queries
      queryClient.setQueriesData({ queryKey: ["prospects", "infinite"] }, (old: unknown) => {
        if (!old || typeof old !== "object" || !("pages" in old)) return old

        const oldData = old as {
          pages: Array<{ prospects: Array<{ id: string; tags?: Tag[] }> }>
        }
        return {
          ...old,
          pages: oldData.pages.map((page) => ({
            ...page,
            prospects: page.prospects.map((prospect) => {
              if (params.prospect_ids.includes(prospect.id)) {
                const updates: Record<string, unknown> = {}
                if (params.custom_field_key) {
                  const v = params.custom_field_value
                  const isComplexValue = typeof v === "string" && (v.startsWith("{") || v.startsWith("<"))
                  if (!isComplexValue) updates[params.custom_field_key] = v
                }
                if (tagToAdd) {
                  const existingTags = prospect.tags || []
                  const alreadyHasTag = existingTags.some((t) => t.id === tagToAdd.id)
                  if (!alreadyHasTag) {
                    updates.tags = [...existingTags, tagToAdd]
                  }
                }
                if (params.remove_tag_id) {
                  updates.tags = (prospect.tags || []).filter((tag) => tag.id !== params.remove_tag_id)
                }
                return { ...prospect, ...updates }
              }
              return prospect
            }),
          })),
        }
      })

      return { previousQueries }
    },
    onError: (err, variables, context) => {
      // Restore all previous queries on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
    },
    onSuccess: (_data, params) => {
      if (params.remove_tag_id) {
        setTimeout(() => queryClient.invalidateQueries({ queryKey: ["prospects"] }), 5000)
        setTimeout(() => queryClient.invalidateQueries({ queryKey: ["prospects"] }), 15000)
        setTimeout(() => queryClient.invalidateQueries({ queryKey: ["prospects"] }), 60000)
      } else {
        queryClient.invalidateQueries({ queryKey: ["prospects"] })
      }
    },
  })
}

export function useBulkPromoteProspects() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkPromoteProspectsPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkPromoteToClient(context, params)
    },
    onSuccess: () => {
      // Invalidate both prospects and clients since promotion moves them
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
      queryClient.invalidateQueries({ queryKey: ["clients"] })
    },
  })
}

export function useBulkCreateProspects() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkCreateProspectsPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkCreateProspects(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
      queryClient.invalidateQueries({ queryKey: ["lists"] })
    },
  })
}

export function useBulkDestroyProspects() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (prospectIds: string[]) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkDestroyProspects(context, prospectIds)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
    },
  })
}

export function useBulkAddProspectsToSequence() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkAddProspectsToSequencePayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkAddProspectsToSequence(context, params)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
      queryClient.invalidateQueries({ queryKey: ["sequence-enrollments", variables.sequence_id] })
      queryClient.invalidateQueries({ queryKey: ["sequence-enrollment-stats"] })
    },
  })
}

export function useBulkAddProspectsToProject() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkAddProspectsToProjectPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkAddProspectsToProject(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
      queryClient.invalidateQueries({ queryKey: ["projects"] })
    },
  })
}

export function useBulkRenameProspects() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkRenameProspectsPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkRenameProspects(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
    },
  })
}
